'use client';

import { Review } from '@/lib/Apis/models';
import { StarRating } from './StarRating';
import { Star, Pencil } from 'lucide-react';

interface RatingDistributionProps {
  reviews: Review[];
  averageRating?: number;
  onWriteReview?: () => void;
}

export function RatingDistribution({ reviews, averageRating, onWriteReview }: RatingDistributionProps) {
  const totalReviews = reviews.length;

  const ratingCounts = reviews.reduce(
    (acc, review) => {
      const rating = Math.floor(review.rating);
      acc[rating] = (acc[rating] || 0) + 1;
      return acc;
    },
    {} as Record<number, number>
  );

  const calculatedAverage =
    averageRating ||
    (totalReviews > 0
      ? reviews.reduce((sum, r) => sum + r.rating, 0) / totalReviews
      : 0);

  const getRatingPercentage = (rating: number) => {
    const count = ratingCounts[rating] || 0;
    return totalReviews > 0 ? (count / totalReviews) * 100 : 0;
  };

  return (
    <div className="rounded-lg border border-gray-200 p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex flex-col md:flex-row gap-8 flex-1">
          <div className="flex flex-col items-center justify-center md:border-r border-gray-200 md:pr-8">
            <div className="text-5xl font-medium text-hsecondary mb-2">
              {calculatedAverage.toFixed(1)}
            </div>
            <StarRating rating={calculatedAverage} size="md" showNumber={false} />
            <p className="text-sm text-hsecondary opacity-50 mt-2">
              Based on {totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}
            </p>
          </div>

          <div className="flex-1 space-y-3">
            {[5, 4, 3, 2, 1].map((rating) => {
              const percentage = getRatingPercentage(rating);
              const count = ratingCounts[rating] || 0;

              return (
                <div key={rating} className="flex items-center gap-3">
                  <div className="flex items-center gap-1 w-16">
                    <span className="text-sm text-hsecondary opacity-50">{rating} star</span>
                  </div>

                  <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-hsecondary transition-all duration-300"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>

                  <span className="text-sm text-gray-600 w-12 text-right">
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {onWriteReview && (
          <button
            onClick={onWriteReview}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-[13px] font-medium text-white hover:opacity-90 bg-hsecondary hover:border-hsecondary transition-colors shrink-0"
          >
            <Pencil className="size-3.5" />
            Write a Review
          </button>
        )}
      </div>

      {totalReviews === 0 && (
        <div className="text-center py-8">
          <Star className="size-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 font-medium mb-1">No reviews yet</p>
          <p className="text-sm text-gray-500">Be the first to review this product!</p>
        </div>
      )}
    </div>
  );
}
